/**
 * Raw mutation service fns for bulk asset approval.
 *
 * Wraps the `AssetApprovalActionsService.bulkApprove/bulkUnapprove` SDK methods
 * (BE shipped in). Both take `{ assetIds }` and return per-asset
 * results (`BulkApprovalResponseDto`) — success/failure per asset, unlike the
 * bulk-*assign* endpoints which return aggregate scalars.
 *
 * Each call suppresses the global error toast (SUPPRESS_ERROR_NOTIFICATION_HEADER)
 * so callers own the error UX via `parseProblemDetail` + `notificationService`,
 * matching `features/assign-asset-approval-chain` and `features/approve-asset-step`.
 */
import {
  AssetApprovalActionsService,
  type BulkApprovalResponseDto,
} from '@inference.engine/workspace-api-client/web';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

export const bulkApprove = async (
  projectId: string,
  assetIds: string[],
): Promise<BulkApprovalResponseDto> => {
  const response = await AssetApprovalActionsService.bulkApprove({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    body: { assetIds },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to bulk-approve assets');
  }

  return response.data;
};

export const bulkUnapprove = async (
  projectId: string,
  assetIds: string[],
): Promise<BulkApprovalResponseDto> => {
  const response = await AssetApprovalActionsService.bulkUnapprove({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    body: { assetIds },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to bulk-unapprove assets');
  }

  return response.data;
};
